import { ApiError } from "@bafe/api-client";
import { QueryClient } from "@tanstack/react-query";

/**
 * Retrying a 4xx just delays the error the customer needs to see, so only
 * failures the client itself marks as retryable are attempted again.
 */
function shouldRetry(failureCount: number, error: unknown): boolean {
	if (failureCount >= 2) return false;
	if (error instanceof ApiError) return error.retryable;
	return true;
}

export function createQueryClient(): QueryClient {
	return new QueryClient({
		defaultOptions: {
			queries: {
				retry: shouldRetry,
				staleTime: 30_000,
				refetchOnWindowFocus: false,
			},
			mutations: { retry: false },
		},
	});
}

export const queryClient = createQueryClient();

export const queryKeys = {
	session: ["session"] as const,
	sessions: ["sessions"] as const,
	organisation: ["organisation"] as const,
	members: ["members"] as const,
	overview: ["overview"] as const,
	storage: ["storage"] as const,
	domains: ["domains"] as const,
	domain: (id: string) => ["domain", id] as const,
	mailboxes: (domainId?: string) => ["mailboxes", domainId ?? "all"] as const,
	/**
	 * Distinct from `mailboxes` because an infinite query stores pages rather
	 * than one envelope, and the two cannot share a cache entry. Still nested
	 * under `mailboxes` so invalidating that prefix refreshes both.
	 */
	mailboxesPaged: (domainId?: string) => ["mailboxes", domainId ?? "all", "paged"] as const,
	mailbox: (id: string) => ["mailbox", id] as const,
	mailboxGrants: (id: string) => ["mailbox", id, "grants"] as const,
	aliases: ["aliases"] as const,
	migrations: ["migrations"] as const,
	billing: ["billing"] as const,
	plans: ["plans"] as const,
	supportCases: ["support-cases"] as const,
	hubRecords: ["hub-records"] as const,
	siteRecords: ["site-records"] as const,
	hostedSites: ["hosted-sites"] as const,
	siteTags: ["site-tags"] as const,
	siteRedirects: ["site-redirects"] as const,
	siteDatabases: ["site-databases"] as const,
	teamOverview: ["team-overview"] as const,
	teamChannels: ["team-channels"] as const,
	teamMembers: (id: string) => ["team-members", id] as const,
	teamMessages: (id: string) => ["team-messages", id] as const,
	socialOverview: ["social-overview"] as const,
	socialChannels: ["social-channels"] as const,
	socialFacebookPages: (selection: string) => ["social-facebook-pages", selection] as const,
	socialPosts: ["social-posts"] as const,
	socialAnalytics: ["social-analytics"] as const,
	engageOverview: ["engage-overview"] as const,
	engageCatalog: ["engage-catalog"] as const,
	engageAccounts: ["engage-accounts"] as const,
	engageConversations: ["engage-conversations"] as const,
	engageMessages: (id: string) => ["engage-messages", id] as const,
	engageNotes: (id: string) => ["engage-notes", id] as const,
	engageAccountMembers: (id: string) => ["engage-account-members", id] as const,
	engageLogin: (id: string) => ["engage-login", id] as const,
	engageLoginChallenge: (id: string) => ["engage-login-challenge", id] as const,
};
